<script setup lang="ts">
import type { SubscriptionPlan } from '~/utils/billing/entitlements';

const { t } = useI18n();
const { plan, planLabel, isPaid, isTeamMember, agencySeat } = useEntitlements();
const { openModal } = useBillingModal();
const { isUae } = useUaeGate();

// Display names keyed by the plan id — logic stays on ids, only the label is translated.
const PLAN_LABEL_KEYS: Record<SubscriptionPlan, string> = {
  MEMBER: 'billing.plans.member',
  PRO: 'billing.plans.pro',
  TEAM: 'billing.plans.team',
};

// Paid users see their plan (Pro / Team); everyone else sees an upgrade CTA.
// isPaid implies a non-null plan; planLabel is only a defensive fallback.
// Team seat holders carry no plan of their own but are on the owner's Team plan.
const showsPlan = computed(() => isPaid.value || isTeamMember.value);
const label = computed(() => {
  if (agencySeat.value) return agencySeat.value.agencyName;
  if (isTeamMember.value) return t('billing.plans.team');
  if (!isPaid.value) return t('billing.planChip.goPro');
  return plan.value ? t(PLAN_LABEL_KEYS[plan.value]) : planLabel.value;
});
</script>

<template>
  <button
    v-if="!isUae"
    type="button"
    class="plan-chip"
    :class="showsPlan ? 'plan-chip--paid' : 'plan-chip--go'"
    @click="agencySeat ? navigateTo('/b2b') : isTeamMember ? navigateTo('/profile') : openModal()"
  >
    <svg
      v-if="!showsPlan"
      class="plan-chip__spark"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l2.4 5.8L20 10l-5.6 2.2L12 18l-2.4-5.8L4 10l5.6-2.2z" />
    </svg>
    {{ label }}
  </button>
</template>

<style scoped lang="scss">
.plan-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(13, 15, 21, 0.1);
  background: transparent;
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(13, 15, 21, 0.55);
  cursor: pointer;
  white-space: nowrap;
  line-height: 1;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    box-shadow 0.15s;

  &__spark {
    flex-shrink: 0;
  }

  // Prominent gold CTA for free / basic users → nudges the upgrade.
  &--go {
    border-color: transparent;
    color: #1c1608;
    font-weight: 700;
    background: linear-gradient(180deg, #e6b23f 0%, #d4a032 100%);
    box-shadow: 0 1px 8px rgba(212, 160, 50, 0.4);

    &:hover {
      background: linear-gradient(180deg, #edbb4b 0%, #c8942a 100%);
      box-shadow: 0 2px 12px rgba(212, 160, 50, 0.55);
    }
  }

  // Subtle gold accent for Pro / Team subscribers (informational, not a CTA)
  &--paid {
    border-color: rgba(212, 160, 50, 0.4);
    color: #8b6000;
    background: rgba(255, 246, 231, 0.7);

    &:hover {
      background: rgba(255, 246, 231, 1);
      border-color: rgba(212, 160, 50, 0.65);
    }
  }
}
</style>
